/**
 * Formatage — module PUR, sans React.
 *
 * Toutes les dates arrivent en ISO 8601 UTC (règle transverse d'`API.md`).
 * L'affichage local est la responsabilité du web, et le fuseau de référence de
 * l'exploitation est **Africa/Dakar** : c'est celui des rapports journaliers
 * (`REPORT_TIMEZONE`). Afficher l'heure du navigateur ferait diverger le
 * dashboard du rapport qu'on envoie au COJOJ.
 */

export const REPORT_TZ = 'Africa/Dakar';

const nfInt = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });
const nfDec = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });

export function formatInt(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'n/d';
  return nfInt.format(value);
}

export function formatDecimal(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'n/d';
  return nfDec.format(value);
}

/** `0.62` → `62 %`. Espace insécable avant le signe, comme le veut le français. */
export function formatPercent(ratio: number | null | undefined, digits = 0): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return 'n/d';
  const nf = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return `${nf.format(ratio * 100)} %`;
}

/** Durée lisible : `214` → `3 min 34 s`. Utilisé pour la médiane de session. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return 'n/d';
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  const rest = s % 60;
  if (m < 60) return rest === 0 ? `${m} min` : `${m} min ${rest} s`;
  const h = Math.floor(m / 60);
  return `${h} h ${String(m % 60).padStart(2, '0')}`;
}

/**
 * Âge d'un point GPS. Volontairement plus court que `formatDuration` : sur la
 * page Réseau on lit une colonne entière de ces valeurs.
 */
export function formatAge(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return 'n/d';
  const s = Math.max(0, Math.round(seconds));
  // Les secondes sont conservées jusqu'à 3 minutes. Le seuil « frais » de la
  // page Réseau est à 2 minutes : arrondir plus tôt afficherait « 2 min » aussi
  // bien pour un point frais que pour un point déjà en retard.
  if (s < 180) return `${s} s`;
  const m = Math.round(s / 60);
  if (m < 90) return `${m} min`;
  return `${Math.round(m / 60)} h`;
}

function fmt(iso: string | null | undefined, options: Intl.DateTimeFormatOptions): string {
  if (!iso) return 'n/d';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'n/d';
  return new Intl.DateTimeFormat('fr-FR', { timeZone: REPORT_TZ, ...options }).format(d);
}

/** `03/11 08:12` — compact, pour un axe ou une cellule de tableau. */
export function formatDateTime(iso: string | null | undefined): string {
  return fmt(iso, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function formatTime(iso: string | null | undefined): string {
  return fmt(iso, { hour: '2-digit', minute: '2-digit' });
}

export function formatDay(iso: string | null | undefined): string {
  return fmt(iso, { day: '2-digit', month: '2-digit' });
}

export function formatLongDay(iso: string | null | undefined): string {
  return fmt(iso, { weekday: 'short', day: '2-digit', month: 'long', year: 'numeric' });
}

/** « il y a 4 min » — pour dire depuis quand un chiffre temps réel n'a pas bougé. */
export function formatSince(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return 'n/d';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'n/d';
  const sec = Math.max(0, Math.round((now - t) / 1000));
  if (sec < 10) return "à l'instant";
  return `il y a ${formatAge(sec)}`;
}

/**
 * Ancienneté écrite en toutes lettres : « depuis 12 minutes ».
 *
 * Volontairement plus bavard que `formatSince` : sur une alerte, l'ancienneté
 * est l'information qui décide de l'urgence, et « il y a 12 min » se lit moins
 * vite qu'une phrase complète. Un horodatage ISO, lui, ne se lit pas du tout —
 * il se calcule, et personne ne calcule à 3 h du matin.
 */
export function formatElapsedLong(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return 'depuis une durée inconnue';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'depuis une durée inconnue';
  const minutes = Math.max(0, Math.round((now - t) / 60_000));
  if (minutes < 1) return "à l'instant";
  if (minutes === 1) return 'depuis 1 minute';
  if (minutes < 60) return `depuis ${minutes} minutes`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    const head = hours === 1 ? 'depuis 1 heure' : `depuis ${hours} heures`;
    return rest === 0 ? head : `${head} et ${rest} min`;
  }
  const days = Math.floor(hours / 24);
  return days === 1 ? 'depuis 1 jour' : `depuis ${days} jours`;
}

/** `2026-11-03` en heure de Dakar — utilisé par les champs `<input type="date">`. */
export function toDateInput(d: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: REPORT_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
  return parts;
}
